import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { AdminCapability } from "shared/utils/capabilities";
import {
  Button,
  Card,
  ConfigProvider,
  Flex,
  Form,
  InputNumber,
  Select,
  Skeleton,
  Switch,
  Typography,
  message,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import BackPageHeading from "components/BackPageHeading.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import PATHS from "shared/constants/paths";
import {
  SettingRow,
  SettingsPage,
  SettingsSection,
  SettingsSections,
  SettingsSectionsFooter,
} from "pages/settings/settingsRows";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { getAllProductsByBusinessId } from "hooks/queries/product";
import { useLoyaltyConfig } from "hooks/queries/loyalty";
import {
  useDeactivateLoyaltyProgram,
  useUpdateLoyaltyConfig,
} from "hooks/mutations/loyalty";
import { showConfirmModal } from "components/modals/confirmModal";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import {
  RewardTypeSpecificFields,
  sameFormValues,
  unitAddon,
} from "pages/settings/rewardSettingsShared";
import { PROGRAM_STATUS_LABELS, PROGRAM_STATUS_TONE } from "shared/utils";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { translate } from "shared/languages";
import {
  ExpiryUnit,
  LoyaltyClassScope,
  LoyaltyDiscountScope,
  LoyaltyProductScope,
  LoyaltyProgramStatus,
  LoyaltyRewardType,
  ProductType,
} from "shared/types/enum";
import { LoyaltyConfig, UpdateLoyaltyConfigForm } from "shared/types/loyalty";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

const { Title, Text } = Typography;

// Milestone row shape as held by the AntD form (before payload mapping).
type MilestoneFormRow = {
  stamp_number?: number;
  reward_type?: string;
  valid_value?: number;
  valid_unit?: string;
  credit_qty?: number;
  class_scope?: string;
  class_type_ids?: string[];
  discount_mode?: string;
  discount_value?: number;
  discount_scope?: string;
  product_ids?: string[];
};

// A new rung of the ladder: no reward type chosen, so no type-specific fields
// render until the studio picks one. Blank for the same reason the activation
// form is (Andrew, 2026-08-20) — a drafted "1 kredit gratis, 30 hari" reads as
// a decision the studio never made. The unit rides along because it is the
// addon on a blank validity input, not a value of its own. Every field here is
// required, so a half-filled rung cannot be saved.
const emptyMilestone: MilestoneFormRow = {
  valid_unit: ExpiryUnit.Day,
};

// A fresh campaign's form values — used when the program is off (default) and
// after a deactivation reset.
//
// Deliberately BLANK (Andrew, 2026-08-20): a pre-filled stamp count read as a
// configured card the studio never chose. "Ulangi kartu setelah selesai" is
// the one thing that starts on. The scope selects keep their "semua" default:
// they are mode pickers with no required rule, not values to fill in.
const emptyFormValues = {
  total_stamps: undefined,
  repeatable: true,
  has_cycle_validity: false,
  cycle_validity_value: undefined,
  cycle_validity_unit: ExpiryUnit.Day,
  qualifying_class_scope: LoyaltyClassScope.All,
  qualifying_class_type_ids: [],
  qualifying_product_scope: LoyaltyProductScope.All,
  qualifying_product_ids: [],
  milestones: [],
};

// Pengaturan -> Sistem Hadiah -> Program Loyalti (any Admin since 2026-08-20;
// no role gate on the route). One full-document form in three sections (the
// card, what earns a stamp, the milestone ladder), with the program status in
// the page heading. Saves confirm with the next-cycle note: running cycles
// keep their frozen snapshot.
const LoyaltySettingsCard = () => {
  // Pembatasan Admin: the form sits inside ReadOnlyWhenRestricted, whose
  // DisabledContext every AntD control inherits, except a control with an
  // explicit `disabled` prop. The add-milestone button, the save button and
  // the heading's turn-off button (outside the wrapper) read the
  // restriction themselves.
  const { isRestricted } = useAdminCapabilities();
  const settingsRestricted = isRestricted(AdminCapability.PromoReward);
  const { data: config, isLoading } = useLoyaltyConfig();
  const { data: classes = [] } = getAllClassTypeByBusinessId();
  const { data: productList } = getAllProductsByBusinessId();
  const updateConfig = useUpdateLoyaltyConfig();
  const deactivateProgram = useDeactivateLoyaltyProgram();
  const [form] = useForm();
  // "Dirty" is derived against a snapshot of the last-known-saved values
  // (set at hydration, and re-set after a successful save/deactivate), not a
  // manually-toggled boolean — a boolean only ever flips true on edit and
  // never derives back to false on its own, the same staleness
  // PolicySettingsPage's dirty flag had before being fixed to this pattern.
  const [baseline, setBaseline] = useState<Record<string, unknown> | null>(null);
  // Off (the default) hides the form behind an explicit "turn on" step;
  // configuring = the manager clicked it and is drafting the new campaign,
  // which goes live on save.
  const [configuring, setConfiguring] = useState(false);

  const status = config?.program?.status ?? LoyaltyProgramStatus.Off;

  const hasValidity = Form.useWatch("has_cycle_validity", form);
  const classScope = Form.useWatch("qualifying_class_scope", form);
  const productScope = Form.useWatch("qualifying_product_scope", form);
  // Live guards for the milestone ladder: stamp numbers are capped at the
  // card total, and once a milestone sits at the total the ladder is complete.
  const totalStamps = Form.useWatch("total_stamps", form);
  const milestoneRows = Form.useWatch("milestones", form) as
    | MilestoneFormRow[]
    | undefined;
  const ladderComplete =
    typeof totalStamps === "number" &&
    (milestoneRows ?? []).some((m) => m?.stamp_number === totalStamps);

  // Hydrate once (see PolicySettingsPage's hydratedRef note). An off program is a
  // finished campaign: its leftover row hydrates as a fresh form.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (isLoading || hydratedRef.current) return;
    hydratedRef.current = true;
    const raw: LoyaltyConfig | null = config?.program ?? null;
    const program = raw && raw.status !== LoyaltyProgramStatus.Off ? raw : null;
    const hydrated = program
      ? {
          total_stamps: program.total_stamps,
          repeatable: program.repeatable,
          has_cycle_validity: program.cycle_validity_value != null,
          cycle_validity_value: program.cycle_validity_value ?? undefined,
          cycle_validity_unit: program.cycle_validity_unit ?? ExpiryUnit.Day,
          // A pre-scope server omits the field; those programs always
          // listed classes explicitly.
          qualifying_class_scope:
            program.qualifying_class_scope ?? LoyaltyClassScope.Selected,
          qualifying_class_type_ids: program.qualifying_class_type_ids,
          qualifying_product_scope:
            program.qualifying_product_scope ?? LoyaltyProductScope.All,
          qualifying_product_ids: program.qualifying_product_ids,
          milestones: program.milestones.map((m) => ({
            stamp_number: m.stamp_number,
            reward_type: m.reward_type,
            valid_value: m.valid_value,
            valid_unit: m.valid_unit ?? ExpiryUnit.Day,
            credit_qty: m.credit_qty ?? undefined,
            class_scope: m.class_scope ?? undefined,
            class_type_ids: m.class_type_ids,
            discount_mode: m.discount_mode ?? undefined,
            discount_value: m.discount_value ?? undefined,
            discount_scope: m.discount_scope ?? undefined,
            product_ids: m.product_ids,
          })),
        }
      : emptyFormValues;
    form.setFieldsValue(hydrated);
    setBaseline(form.getFieldsValue(true));
  }, [config, isLoading, form]);

  const watchedValues = Form.useWatch([], { form, preserve: true });
  const dirty =
    !!baseline && !!watchedValues && !sameFormValues(watchedValues, baseline);

  const products = useMemo(() => productList?.products ?? [], [productList]);
  // Whole catalog products (never tiers), Paket group first then Membership —
  // the same split the catalog pages use.
  const productOptions = useMemo(() => {
    const toOption = (p: { id: string; name: string }) => ({
      value: p.id,
      label: p.name,
    });
    const credit = products
      .filter((p) => p.product_type === ProductType.Credit)
      .map(toOption);
    const memberships = products
      .filter((p) => p.product_type === ProductType.Membership)
      .map(toOption);
    const groups = [];
    if (credit.length > 0) {
      groups.push({
        label: translate("package"),
        title: translate("package"),
        options: credit,
      });
    }
    if (memberships.length > 0) {
      groups.push({
        label: translate("membership"),
        title: translate("membership"),
        options: memberships,
      });
    }
    return groups;
  }, [products]);

  const heading = (extra?: ReactNode) => (
    <BackPageHeading
      title={translate("loyalty.card_title")}
      backTo={PATHS.SETTINGS}
      extra={extra}
    />
  );

  if (isLoading) {
    return (
      <SettingsPage>
        {heading()}
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </SettingsPage>
    );
  }

  // Off by default: businesses opt in. The form only appears after the
  // explicit "turn on" step; the program itself goes live on the first save.
  if (status === LoyaltyProgramStatus.Off && !configuring) {
    return (
      <SettingsPage>
        {heading()}
        <ReadOnlyWhenRestricted capability={AdminCapability.PromoReward}>
          <Card>
            <Flex
              vertical
              align="center"
              gap={6}
              style={{ padding: "36px 16px", textAlign: "center" }}
            >
              <Title level={5} style={{ margin: 0 }}>
                {translate("loyalty.off.title")}
              </Title>
              <Text type="secondary" style={{ fontSize: textPx(13), maxWidth: 420 }}>
                {translate("loyalty.off.body")}
              </Text>
              <Button
                type="primary"
                style={{ marginTop: 14 }}
                onClick={() => setConfiguring(true)}
              >
                {translate("loyalty.off.cta")}
              </Button>
            </Flex>
          </Card>
        </ReadOnlyWhenRestricted>
      </SettingsPage>
    );
  }

  const classOptions = classes.map((c) => ({ value: c.id, label: c.name }));

  const onSave = async () => {
    const v = await form.validateFields().catch(() => null);
    if (!v) return;
    const milestones = ((v.milestones ?? []) as MilestoneFormRow[]).map(
      (m) => ({
        stamp_number: Number(m.stamp_number),
        reward_type: m.reward_type!,
        valid_value: Number(m.valid_value),
        valid_unit: m.valid_unit!,
        ...(m.reward_type === LoyaltyRewardType.FreeCredit
          ? {
              credit_qty: Number(m.credit_qty),
              class_scope: m.class_scope,
              ...(m.class_scope === LoyaltyClassScope.Selected
                ? { class_type_ids: m.class_type_ids ?? [] }
                : {}),
            }
          : {
              discount_mode: m.discount_mode,
              discount_value: Number(m.discount_value),
              discount_scope: m.discount_scope,
              ...(m.discount_scope === LoyaltyDiscountScope.Products
                ? { product_ids: m.product_ids ?? [] }
                : {}),
            }),
      })
    );
    // The input guards keep numbers <= total and distinct; this catches the
    // one shape they can't: a ladder that never reaches the card total.
    const highest = Math.max(0, ...milestones.map((m) => m.stamp_number));
    if (highest !== Number(v.total_stamps)) {
      message.error(translate("loyalty.error.last_milestone"));
      return;
    }
    const payload: UpdateLoyaltyConfigForm = {
      total_stamps: Number(v.total_stamps),
      repeatable: !!v.repeatable,
      cycle_validity_value: v.has_cycle_validity
        ? Number(v.cycle_validity_value)
        : null,
      cycle_validity_unit: v.has_cycle_validity ? v.cycle_validity_unit : null,
      qualifying_class_scope: v.qualifying_class_scope,
      ...(v.qualifying_class_scope === LoyaltyClassScope.Selected
        ? { qualifying_class_type_ids: v.qualifying_class_type_ids ?? [] }
        : {}),
      qualifying_product_scope: v.qualifying_product_scope,
      ...(v.qualifying_product_scope === LoyaltyProductScope.Products
        ? { qualifying_product_ids: v.qualifying_product_ids ?? [] }
        : {}),
      milestones,
    };
    showConfirmModal({
      tone: "amber",
      title: translate("loyalty.confirm.title"),
      content: translate("loyalty.confirm.body"),
      confirmLabel: translate("button.save_changes"),
      onConfirm: () =>
        updateConfig.mutate(payload, {
          onSuccess: () => {
            // v is exactly what just got saved — the new baseline to diff
            // against, without waiting on a refetch.
            setBaseline(form.getFieldsValue(true));
            setConfiguring(false);
          },
        }),
    });
  };

  const onDeactivate = () => {
    showConfirmModal({
      tone: "error",
      title: translate("loyalty.lifecycle.deactivate_confirm_title"),
      content: translate("loyalty.lifecycle.deactivate_confirm_body"),
      confirmLabel: translate("loyalty.lifecycle.deactivate"),
      onConfirm: () =>
        deactivateProgram.mutate(undefined, {
          onSuccess: () => {
            // Back to the off empty state with a fresh form for the next
            // campaign.
            setConfiguring(false);
            // resetFields FIRST: setFieldsValue MERGES, and it recurses into
            // arrays too, so `milestones: []` alone would leave the previous
            // campaign's ladder in place for a same-session reactivate.
            form.resetFields();
            form.setFieldsValue(emptyFormValues);
            setBaseline(form.getFieldsValue(true));
          },
        }),
    });
  };

  // Program status sits in the page heading: the badge and the turn-off
  // action belong to the whole page, not to one group of settings. The
  // heading is outside the read-only wrapper, so the button reads the
  // restriction itself.
  const headingExtra =
    status === LoyaltyProgramStatus.Off ? (
      <Text type="secondary" style={{ fontSize: textPx(13) }}>
        {translate("loyalty.lifecycle.activate_note")}
      </Text>
    ) : (
      <Flex align="center" gap={12}>
        <StatusBadge tone={PROGRAM_STATUS_TONE} label={PROGRAM_STATUS_LABELS.active} />
        <Button
          danger
          size="small"
          disabled={settingsRestricted}
          loading={deactivateProgram.isPending}
          onClick={onDeactivate}
        >
          {translate("loyalty.lifecycle.deactivate")}
        </Button>
      </Flex>
    );

  const scopeSelectStyle = { width: 220 };

  return (
    <SettingsPage>
      {heading(headingExtra)}
      <Flex vertical gap={16}>
        <ReadOnlyWhenRestricted capability={AdminCapability.PromoReward}>
          <Form form={form} layout="vertical" requiredMark={false}>
            <SettingsSections>
              {/* ---- The card itself ---- */}
              <SettingsSection title={translate("loyalty.section.card")}>
                <SettingRow
                  title={translate("loyalty.field.total_stamps")}
                  hint={translate("loyalty.field.total_stamps_hint")}
                  control={
                    <Form.Item
                      name="total_stamps"
                      style={{ margin: 0 }}
                      rules={[
                        {
                          required: true,
                          type: "integer",
                          min: 1,
                          message: translate("form.error.required"),
                        },
                      ]}
                    >
                      <InputNumber
                        min={1}
                        precision={0}
                        style={{ width: 120 }}
                        aria-label={translate("loyalty.field.total_stamps")}
                      />
                    </Form.Item>
                  }
                />
                <SettingRow
                  title={translate("loyalty.field.repeatable")}
                  hint={translate("loyalty.field.repeatable_hint")}
                  control={
                    <Form.Item name="repeatable" valuePropName="checked" noStyle>
                      <Switch aria-label={translate("loyalty.field.repeatable")} />
                    </Form.Item>
                  }
                />
                <SettingRow
                  title={translate("loyalty.field.has_validity")}
                  hint={translate("loyalty.field.has_validity_hint")}
                  last
                  control={
                    <Form.Item name="has_cycle_validity" valuePropName="checked" noStyle>
                      <Switch aria-label={translate("loyalty.field.has_validity")} />
                    </Form.Item>
                  }
                  below={
                    hasValidity && (
                      <Form.Item
                        name="cycle_validity_value"
                        style={{ margin: "12px 0 0" }}
                        rules={[
                          {
                            required: true,
                            type: "integer",
                            min: 1,
                            message: translate("form.error.required"),
                          },
                        ]}
                      >
                        <InputNumber
                          min={1}
                          precision={0}
                          style={{ width: 200 }}
                          aria-label={translate("loyalty.field.validity_days")}
                          addonAfter={unitAddon(["cycle_validity_unit"])}
                        />
                      </Form.Item>
                    )
                  }
                />
              </SettingsSection>

              {/* ---- What earns a stamp ---- */}
              <SettingsSection title={translate("loyalty.section.earning")}>
                <SettingRow
                  title={translate("loyalty.field.qualifying")}
                  hint={translate("loyalty.field.qualifying_hint")}
                  control={
                    <Form.Item name="qualifying_class_scope" style={{ margin: 0 }}>
                      <Select
                        style={scopeSelectStyle}
                        aria-label={translate("loyalty.field.qualifying")}
                        options={[
                          { value: LoyaltyClassScope.All, label: translate("loyalty.scope.all_classes") },
                          { value: LoyaltyClassScope.AllOpen, label: translate("loyalty.scope.all_open") },
                          { value: LoyaltyClassScope.AllPrivate, label: translate("loyalty.scope.all_private") },
                          { value: LoyaltyClassScope.Selected, label: translate("loyalty.scope.selected") },
                        ]}
                      />
                    </Form.Item>
                  }
                  below={
                    classScope === LoyaltyClassScope.Selected && (
                      <Form.Item
                        name="qualifying_class_type_ids"
                        style={{ margin: "12px 0 0" }}
                        rules={[{ required: true, message: translate("loyalty.error.qualifying") }]}
                      >
                        <Select
                          mode="multiple"
                          options={classOptions}
                          optionFilterProp="label"
                          placeholder={translate("loyalty.field.qualifying_placeholder")}
                        />
                      </Form.Item>
                    )
                  }
                />
                <SettingRow
                  title={translate("loyalty.field.qualifying_products")}
                  hint={translate("loyalty.field.qualifying_products_hint")}
                  last
                  control={
                    <Form.Item name="qualifying_product_scope" style={{ margin: 0 }}>
                      <Select
                        style={scopeSelectStyle}
                        aria-label={translate("loyalty.field.qualifying_products")}
                        options={[
                          { value: LoyaltyProductScope.All, label: translate("loyalty.product_scope.all") },
                          { value: LoyaltyProductScope.Packages, label: translate("loyalty.product_scope.packages") },
                          { value: LoyaltyProductScope.Memberships, label: translate("loyalty.product_scope.memberships") },
                          { value: LoyaltyProductScope.Products, label: translate("loyalty.product_scope.products") },
                        ]}
                      />
                    </Form.Item>
                  }
                  below={
                    productScope === LoyaltyProductScope.Products && (
                      <Form.Item
                        name="qualifying_product_ids"
                        style={{ margin: "12px 0 0" }}
                        rules={[{ required: true, message: translate("loyalty.error.qualifying_products") }]}
                      >
                        <Select mode="multiple" options={productOptions} optionFilterProp="label" />
                      </Form.Item>
                    )
                  }
                />
              </SettingsSection>

              {/* ---- Milestone ladder ---- */}
              <SettingsSection
                title={translate("loyalty.section.milestones")}
                description={translate("loyalty.milestones.hint")}
              >
                <Form.List name="milestones">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name }, index) => (
                        <div
                          key={key}
                          style={{
                            padding: "16px 0 0",
                            borderBottom: `1px solid ${colors.line}`,
                          }}
                        >
                          <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                            <span style={{ fontWeight: 600, fontSize: textPx(14.5) }}>
                              {translate("loyalty.milestones.item_eyebrow")} {index + 1}
                            </span>
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              aria-label={translate("button.delete")}
                            />
                          </Flex>

                          <div className="reward-fields-grid">
                            <Form.Item
                              name={[name, "stamp_number"]}
                              label={translate("loyalty.milestones.stamp_number")}
                              rules={[
                                {
                                  required: true,
                                  type: "integer",
                                  min: 1,
                                  message: translate("form.error.required"),
                                },
                                ...(typeof totalStamps === "number"
                                  ? [
                                      {
                                        type: "integer" as const,
                                        max: totalStamps,
                                        message: translate("loyalty.error.stamp_exceeds"),
                                      },
                                    ]
                                  : []),
                                ({ getFieldValue }: { getFieldValue: (p: unknown) => unknown }) => ({
                                  validator: (_: unknown, value: number) => {
                                    if (value == null) return Promise.resolve();
                                    const rows = (getFieldValue("milestones") ?? []) as MilestoneFormRow[];
                                    const uses = rows.filter((r) => r?.stamp_number === value).length;
                                    return uses > 1
                                      ? Promise.reject(new Error(translate("loyalty.error.duplicate_stamp")))
                                      : Promise.resolve();
                                  },
                                }),
                              ]}
                            >
                              <InputNumber
                                min={1}
                                max={typeof totalStamps === "number" ? totalStamps : undefined}
                                precision={0}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, "reward_type"]}
                              label={translate("loyalty.milestones.reward_type")}
                              rules={[{ required: true, message: translate("form.error.required") }]}
                            >
                              <Select
                                options={[
                                  { value: LoyaltyRewardType.FreeCredit, label: translate("loyalty.reward.free_credit") },
                                  { value: LoyaltyRewardType.Discount, label: translate("loyalty.reward.discount") },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, "valid_value"]}
                              label={translate("loyalty.milestones.valid_days")}
                              rules={[
                                {
                                  required: true,
                                  type: "integer",
                                  min: 1,
                                  message: translate("form.error.required"),
                                },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                precision={0}
                                style={{ width: "100%" }}
                                addonAfter={unitAddon([name, "valid_unit"])}
                              />
                            </Form.Item>

                            {/* Reward-type-specific fields (shared with the
                                referral reward builders) */}
                            <RewardTypeSpecificFields
                              namePath={[name]}
                              fullPath={["milestones", name]}
                              classOptions={classOptions}
                              productOptions={productOptions}
                            />
                          </div>
                        </div>
                      ))}
                      {/* Solid ink pill — the package detail's "Tambah Tier"
                          treatment. Disabled once a milestone sits at the
                          card total (the ladder is complete). */}
                      <div style={{ padding: "16px 0" }}>
                        <ConfigProvider theme={modalButtonsTheme}>
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            disabled={ladderComplete || settingsRestricted}
                            style={{ borderRadius: 9999, paddingInline: 15 }}
                            onClick={() => add({ ...emptyMilestone })}
                          >
                            {translate("loyalty.milestones.add")}
                          </Button>
                        </ConfigProvider>
                      </div>
                    </>
                  )}
                </Form.List>
              </SettingsSection>

              <SettingsSectionsFooter>
                {dirty && (
                  <span
                    aria-label="unsaved"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: colors.amber,
                      display: "inline-block",
                    }}
                  />
                )}
                <Button
                  type="primary"
                  disabled={!dirty || settingsRestricted}
                  loading={updateConfig.isPending}
                  onClick={onSave}
                >
                  {translate("button.save_changes")}
                </Button>
              </SettingsSectionsFooter>
            </SettingsSections>
          </Form>
        </ReadOnlyWhenRestricted>
      </Flex>
    </SettingsPage>
  );
};

export default LoyaltySettingsCard;
